import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect, useMemo } from 'react'
import styled, { css } from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { CurrencyIcon } from '@root/entities/currency/ui'
import { SwapButton } from '@root/entities/trade-type/ui'
import { useTranslation } from '@root/shared/localization'
import { Input } from '@root/shared/ui-kit'

import {
  instantBuyQuickOptionsAtom,
  reatomInstantBuy,
} from '../../atoms/reatom-instant-buy'

import { InstantTradeBottomDirectionLabel } from './instant-trade-bottom-direction-label'
import { InstantTradeBottomGas } from './instant-trade-bottom-gas'
import { InstantTradeBottomQuickOption } from './instant-trade-bottom-quick-option'

import type { PairModel } from '@root/entities/pair'
import type { ClassNameProps } from '@root/shared/react'

type InstantTradeBottomBuyProps = {
  pair: PairModel
} & ClassNameProps

export const InstantTradeBottomBuy: React.FC<InstantTradeBottomBuyProps> = ({
  className,
  pair,
}) => {
  const { t } = useTranslation()

  // eslint-disable-next-line @reatom/unit-naming-rule
  const model = useMemo(() => reatomInstantBuy({ pair }), [pair])
  const { amountInputModel } = model

  const [amount] = useAtom(amountInputModel.amountAtom)
  const [amountRaw] = useAtom(amountInputModel.amountRawAtom)
  const [requestState] = useAtom(model.executeSwapModel)
  const [validationError] = useAtom(model.validationAtom)
  const setAmountRaw = useAction(amountInputModel.setAmountRawAction)
  const executeBuy = useAction(model.executeSwapModel.execute)
  const initialize = useAction(model.initialize)

  const [instantBuyQuickOptions] = useAtom(instantBuyQuickOptionsAtom)

  useEffect(() => {
    const dispose = initialize()
    return () => {
      dispose()
    }
  }, [initialize, model])

  return (
    <Root className={className}>
      <InstantTradeBottomDirectionLabel direction="buy" pair={pair} />

      <InputContainer>
        <AmountInput
          numeric
          startContent={
            <InputLabel>
              <QuickOptionsContainer>
                {instantBuyQuickOptions.map((it) => (
                  <InstantTradeBottomQuickOption
                    label={it.toString()}
                    key={it.toNumber()}
                    active={amount.eq(it)}
                    onClick={() => setAmountRaw(it.toString())}
                  />
                ))}
              </QuickOptionsContainer>
              <span
                css={css`
                  font-weight: 400;
                `}
              >
                {t('common.amount')}
              </span>
            </InputLabel>
          }
          decimalScale={pair.base.decimals}
          onChange={(e) => setAmountRaw(e.target.value)}
          value={amountRaw}
          size="m"
          endContent={
            <CurrencyIcon
              currency={pair.base}
              size={24}
              withGoogleLens={false}
              css={css`
                justify-self: end;
              `}
            />
          }
        />
      </InputContainer>
      <BuyButton
        swapDirection="buy"
        onClick={() => executeBuy()}
        disabled={!!validationError}
        busy={AsyncStateN.isLoading(requestState)}
      >
        {t('trading.buy')}
      </BuyButton>
      <InstantTradeBottomGas swapDirection="buy" />
    </Root>
  )
}

const Root = styled.div`
  padding-inline: 12px 12px;
  align-content: center;

  display: grid;
  grid-auto-flow: column;
`

const BuyButton = styled(SwapButton)`
  width: 62px;
  height: 36px;
`

const AmountInput = styled(Input)`
  width: 320px;
  --input-height: 36px;
  --input-padding-inline: 3px 6px;
`

const InputContainer = styled.div`
  padding-top: 1px;
  margin-inline-end: 6px;
`

const QuickOptionsContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 2px;
`

const InputLabel = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 10px;
  align-items: center;
`
